/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import Cookies from 'js-cookie'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {getLocaleByReference, getParamsFromPath} from './utils'
import {getDefaultSite, getSiteByReference} from './site-utils'
import {HOME_HREF, ORDERSTATUS_HREF, urlPartPositions} from '../constants'
import {encodeUrlLocale} from './locale'
import {Buffer} from 'buffer'

export const isAbsoluteUrl = (url) => url.startsWith('//') || url.indexOf('://') > 0

/**
 * Constructs an absolute URL from a given path and an optional application origin.
 *
 * @param {string} path - The relative URL path to be appended to the origin.
 * @param {string} [appOrigin] - The optional application origin (e.g., "https://example.com").
 *                                If not provided, the function will call `getAppOrigin()`.
 * @returns {string} - The fully qualified URL as a string.
 */
export const absoluteUrl = (path, appOrigin) => {
    return new URL(path, appOrigin || getAppOrigin()).toString()
}

/**
 * Modifies a given url by adding/updating query parameters.
 *
 * @param {string} url - The base url of the output url set.
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string} A URL with additional params
 * @example
 * import {rebuildPathWithParams} from '/path/to/utils/url'
 *
 * rebuildPathWithParams(
 *     '/en-gb/product/25501032M',
 *     {color: 'JJ2SKXX', size: 'MD'}
 * )
 *
 * // Returns
 * // '/en-gb/product/25501032M?color=JJ2SKXX&size=9MD'
 */
export const rebuildPathWithParams = (url, extraParams) => {
    const [pathname, search] = url.split('?')
    const params = new URLSearchParams(search)

    updateSearchParams(params, extraParams)

    // Clean up any trailing `=` for params without values.
    // Replace any `+` with `%20`
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '').replace(/\+/g, '%20')

    // Generate the newly updated url.
    return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ''}`
}

export const updateSearchParams = (searchParams, newParams) => {
    Object.entries(newParams).forEach(([key, value]) => {
        // 0 is a valid value as for a param
        if (!value && value !== 0) {
            searchParams.delete(key)
        } else {
            searchParams.set(key, value)
        }
    })
}

/**
 * Builds a list of modified Urls with the provided params key and values,
 * preserving any search params provided in the original url.Optionally
 * you can pass and object used to set static params values.
 * @param {string} url - The base url of the output url set.
 * @param {string} key - The search params for the associated values
 * @param {Array} values - The search param values
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string[]} A list of URLs
 * @example
 * import {buildUrlSet} from '/path/to/utils/url'
 *
 * buildUrlSet(
 *     '/womens/clothing',
 *     'sort',
 *     ['price-high-to-low', 'price-low-to-high'],
 *     {offset: 0}
 * )
 *
 * // Returns
 * // ['/womens/clothing?sort=price-high-to-low', '/womens/clothing?sort=price-low-to-high']
 */
export const buildUrlSet = (url = '', key = '', values = [], extraParams = {}) =>
    values.map((value) => rebuildPathWithParams(url, {[key]: value, ...extraParams}))

/**
 * Given a category and the current locale returns an href to the product list page.
 *
 * @param {Object} category
 * @returns {string}
 */
export const categoryUrlBuilder = (category) =>
    encodeURI(category.c_slugUrl ? `${category.c_slugUrl}` : `/category/${category.id}`)

/**
 * Given a content id returns a href to the customer service content page.
 *
 * @param {String} contentId
 * @returns {string}
 */
export const customerServiceUrlBuilder = (contentId) =>
    encodeURI(`/customer-service/${`${contentId}`.toLowerCase()}/${contentId}.html`)

export const orderCancellationUrlBuilder = (order) =>
    encodeURI(
        `/cancel-order/${Buffer.from(order.orderNo + ':' + order.c_orderCancelToken).toString(
            'base64'
        )}`
    )

export const orderStatusUrlBuilder = (order) => {
    const postalCode =
        order?.shipments?.[0]?.shippingAddress?.postalCode || order?.billingAddress?.postalCode

    if (!order?.orderNo || !postalCode) {
        return undefined
    }

    return rebuildPathWithParams(ORDERSTATUS_HREF, {
        comingFromSearch: true,
        orderNo: order.orderNo,
        zip: postalCode
    })
}

/**
 * Given a product and the current locale returns an href to the product detail page.
 * If a slugUrl is passed then this is used.
 *
 * @param {Object} product
 * @param {string} locale
 * @param {string} [slugUrl] URL created by SFCC
 * @returns {string}
 */
export const productUrlBuilder = (
    product,
    locale,
    slugUrl = product?.slugUrl || product?.c_slugUrl
) => {
    if (slugUrl && locale) {
        return normalizeSfccLink(locale, slugUrl)
    } else {
        return encodeURI(`/product/${product.productId || product.id}`)
    }
}

/**
 * Given a search term, constructs a search url.
 *
 * @param {string} searchTerm
 * @returns {string}
 */
export const searchUrlBuilder = (searchTerm) => '/search?q=' + encodeURIComponent(searchTerm)

/**
 * Extract the path with query params from a given url without the site and locale parameters.
 *
 * @param {Location} location
 * @returns {{pathname: string, search: string, siteRef: string, localeRef: string}}
 */
export const getPathParamsSplit = (location) => {
    let {siteRef, localeRef} = getParamsFromPath(`${location.pathname}${location.search}`)
    let {pathname, search} = location

    // sanitize the site from current url if existing
    if (siteRef) {
        pathname = pathname.replace(`/${siteRef}`, '')
        search = search.replace(`site=${siteRef}`, '')
    }
    // sanitize the locale from current url if existing
    if (localeRef) {
        pathname = pathname.replace(`/${encodeUrlLocale(localeRef)}`, '')
        search = search.replace(`locale=${encodeUrlLocale(localeRef)}`, '')
    }
    // remove ending any &
    search = search.replace(/&$/, '')

    return {pathname, search, siteRef, localeRef}
}

/**
 * Returns a relative URL for a locale short code.
 * Based on your app configuration, this function will replace your current locale shortCode with a new one
 *
 * @param {String} shortCode - The locale short code.
 * @param {function(*, *, *, *=): string} - Generates a site URL from the provided path, site and locale.
 * @param {string[]} opts.disallowParams - URL parameters to remove
 * @param {Object} opts.location - location object to replace the default `window.location`
 * @returns {String} url - The relative URL for the specific locale.
 */
export const getPathWithLocale = (shortCode, buildUrl, opts = {}) => {
    const location = opts.location ? opts.location : window.location

    const {pathname, search, siteRef} = getPathParamsSplit(location)

    const defaultSite = opts?.site || getDefaultSite()

    // Remove query parameters
    const {disallowParams = []} = opts

    let queryString = new URLSearchParams(`${search}`)

    if (disallowParams.length) {
        disallowParams.forEach((param) => {
            queryString.delete(param)
        })
    }

    const site = getSiteByReference(siteRef)

    const locale = getLocaleByReference(site, shortCode)

    // rebuild the url with new locale,
    const newUrl = buildUrl(
        `${pathname}${Array.from(queryString).length !== 0 ? `?${queryString}` : ''}`,
        // By default, as for home page, when the values of site and locale belongs to the default site,
        // they will be not shown in the url just
        defaultSite.alias || defaultSite.id,
        locale?.alias || locale?.id
    )
    return newUrl
}

/**
 * Generates the URL Template literal (Template string) used to build URLs in the App according
 * the current selected site/locale and the default App URL configuration.
 *
 * @param appConfig Application default configuration.
 * @param siteRef Current selected Site reference. The value can be the Site id or alias.
 * @param localeRef Current selected Locale reference. The value can be the Locale id or alias.
 * @returns {function(*, *, *): string} function providing: path, site and locale generates a URL.
 */
export const createUrlTemplate = (appConfig, siteRef, localeRef) => {
    const {site: siteConfig, locale: localeConfig, showDefaults: showDefaultsConfig} = appConfig.url
    const defaultSite = getDefaultSite()
    const site = getSiteByReference(siteRef)
    const defaultLocale = getLocaleByReference(site, site.l10n.defaultLocale)

    const isDefaultSite =
        defaultSite.id === siteRef || (defaultSite.alias && defaultSite.alias === siteRef)
    const isDefaultLocale =
        defaultLocale.id === localeRef || (defaultLocale.alias && defaultLocale.alias === localeRef)

    const querySite =
        (siteConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultSite)
    const queryLocale =
        (localeConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultLocale)

    const isQuery = querySite || queryLocale

    const pathSite =
        (siteConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultSite)
    const pathLocale =
        (localeConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultLocale)

    return (path, site, locale) => {
        const isHomeWithDefaultSiteAndLocale =
            path === HOME_HREF &&
            (defaultSite.id === site || (defaultSite.alias && defaultSite.alias === site)) &&
            (defaultLocale.id === locale || (defaultLocale.alias && defaultLocale.alias === locale))

        // for STOKKE
        // - the HOME_HREF is /home
        // - site and locale is always included
        //   except for default site "stokke" where the site is NOT included
        // - locale is ALL lower case eg. en-gb
        // const sitePath = pathSite && site && !isHomeWithDefaultSiteAndLocale ? `/${site}` : ''
        // const localePath =
        //     pathLocale && locale && !isHomeWithDefaultSiteAndLocale
        //         ? `/${encodeUrlLocale(locale)}`
        //         : ''
        const sitePath =
            pathSite &&
            site &&
            !(defaultSite.id === site || (defaultSite.alias && defaultSite.alias === site))
                ? `/${site}`
                : ''
        const localePath = pathLocale && locale ? `/${encodeUrlLocale(locale)}` : ''

        const hasQuery = isQuery && (site || locale) && !isHomeWithDefaultSiteAndLocale
        let queryString = ''
        if (hasQuery) {
            const searchParams = new URLSearchParams()
            querySite && site && searchParams.append('site', site)
            queryLocale && locale && searchParams.append('locale', locale)
            queryString = `?${searchParams.toString()}`
        }
        return `${sitePath}${localePath}${path}${queryString}`
    }
}

/*
 * Remove query params from a give url path based on a given list of keys
 *
 * @param {string} path - The part of url to have params removed from.
 * @param {array} keys - list of params to be removed
 * @returns {string} - the url after param has been removed
 * @example
 * import {removeQueryParamsFromPath} from /path/to/util/url
 *
 * removeQueryParamsFromPath(
 *   /en-gb/cart?pid=1234&color=black&size=s&abc=12,
 *   ['pid', 'color', 'size']
 * )
 * // returns
 * // '/en-gb/cart?abc=12'
 */
export const removeQueryParamsFromPath = (path, keys) => {
    const [pathname, search] = path.split('?')
    const params = new URLSearchParams(search)
    keys.forEach((key) => {
        if (params.has(key)) {
            params.delete(key)
        }
    })

    // Clean up any trailing `=` for params without values.
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '')

    return `${pathname}${paramStr && '?'}${paramStr}`
}

/*
 * Remove site alias and locale from a given url, to be used for "navigate" urls
 *
 * @param {string} pathName - The part of url to have site alias and locale removed from
 * @returns {string} - the path after site alias and locale have been removed
 * @example
 * import {removeSiteLocaleFromPath} from /path/to/util/url
 *
 * removeSiteLocaleFromPath(/RefArch/en-US/account/wishlist)
 * // returns '/account/wishlist'
 */
export const removeSiteLocaleFromPath = (pathName = '') => {
    let {siteRef, localeRef} = getParamsFromPath(pathName)

    // remove the site alias from the current pathName
    if (siteRef) {
        pathName = pathName.replace(new RegExp(`/${siteRef}`, 'g'), '')
    }
    // remove the locale from the current pathName
    if (localeRef) {
        localeRef = encodeUrlLocale(localeRef)
        pathName = pathName.replace(new RegExp(`/${localeRef}`, 'g'), '')
    }

    return pathName
}

/*
 * Searches and gets parameter value inside a location instance
 *
 * @param {Location} location - the instance with location data
 * @param {string} parameterName - the name of the parameter that is used for search
 * @returns {string} - the parameter value or null if it does not exist
 */
export const getParameterValue = (location, parameterName) => {
    const params = location.search.substring(1).split('&')
    for (let i = 0; i < params.length; i++) {
        let values = params[i].split('=')
        if (values[0] === parameterName) {
            // https://stackoverflow.com/questions/17010119/decodeuri-decodes-space-as-symbol
            return values[1].replace(/\+/g, ' ')
        }
    }
    return null
}

/*
 * Extract the file name from a url
 */
export const getFileNameFromUrl = (url) => {
    return url.split('?', 2).shift().split('/').pop()
}

/**
 * This replaces any dwvar_<pid>_<variatio attribute name> parameter
 * as it is used in SiteGenesis with a simple <variatio attribute name> param.
 * @param {String} search a search string
 * @returns normalized search string
 */
export const normalizeVariationParams = (search) => {
    const regex = /dwvar_\d+?_([a-zA-Z]+=)/gm
    return search.replace(regex, `$1`)
}

/**
 * Fix ampersand
 * @param {String} url the URL to fix
 * @returns an URL where every &amp; is replaced by &
 */
export const fixAmpersand = (url) => (url ? url.replace(/&amp;/g, '&') : url)

/**
 * Remove trailing slash from a given path
 * @param {string} path
 * @return {string}
 */
export const removeTrailingSlash = (path) => path?.replace(/\/$/, '')

/**
 * normalize sfcc link
 *
 * @param {string} locale
 * @param {string} slugUrl URL created by SFCC
 * @returns {string}
 */
export const normalizeSfccLink = (locale, slugUrl) => {
    if (slugUrl && locale) {
        const localeSlug = `/${encodeUrlLocale(locale)}`
        const idx = slugUrl.indexOf(localeSlug)
        return slugUrl.substring(idx + localeSlug.length)
    } else {
        return slugUrl
    }
}

/*
 * Gets redirect to corresponding home page depends on passes country code.
 * MIgrate Default-Start controller in PWA side. Code is executed if it is not server side execution due to the need of
 * window.navigator.language - the only way to get the real language and use it to find the right site and locale.
 * @param {String} countryCode is the country code extracted from the request headers
 * @returns {String} corresponding hope page URL string
 */
export const getCorrespondingHomePage = (countryCode) => {
    let resultPath
    const config = getConfig()
    config.app.sites.forEach((site) => {
        site?.l10n?.supportedLocales.forEach((siteLocale) => {
            // check if the country code based on request path is part of any of the site locales
            if (siteLocale?.id?.split('-')?.[1] === countryCode) {
                const newLocale = siteLocale.id.toLowerCase()
                // set cookie the exact way the controller does on SFCC side
                Cookies.set(
                    'dw_country_and_locale',
                    site.id + '|' + siteLocale.id.replace('-', '_')
                )
                resultPath = `/${site.id}/${newLocale}`
            }
        })
    })
    return resultPath
}

/**
 * Removes environment (dev, stg, www) and domain from a given URL, returning a relative path
 * so it can be used in client-side navigation.
 * @param {string} url
 * @returns {string}
 */
export const convertToRelativePath = (url) =>
    url?.replace(/https?:\/\/((dev|stg|www)\.)?stokke\.com\//i, '/')

/**
 * Resolves an asset URL (videos, PDFs, etc. - not images) relative to the sharedLibraryStatic base URL.
 * Uses the URL constructor to handle both absolute and relative URLs.
 *
 * Note: This is for non-image assets in the shared library. For images, use getDisBaseUrl which
 * leverages Salesforce Dynamic Image Service with the sharedLibraryDis URL.
 *
 * @param {string} assetSrc - The asset source URL (can be relative or absolute)
 * @param {string} [sharedLibraryStatic] - The shared library static base URL from OCAPI imageUrls.sharedLibraryStatic
 * @returns {string|null} The resolved asset URL or null if the URL is invalid
 *
 * @example
 * // Relative URL for a video
 * const assetSrc = 'cms_assets/videos/product-demo.mp4'
 * const sharedLibraryStatic = 'https://dev.stokke.com/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/de_DE/v1761758784946/'
 * getSharedLibraryAssetUrl(assetSrc, sharedLibraryStatic)
 * // Returns: 'https://dev.stokke.com/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/de_DE/v1761758784946/cms_assets/videos/product-demo.mp4'
 *
 * @example
 * // Relative URL with leading slash (treated as relative to sharedLibraryStatic)
 * const assetSrc = '/cms_assets/videos/product-demo.mp4'
 * const sharedLibraryStatic = 'https://dev.stokke.com/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/de_DE/v1761758784946/'
 * getSharedLibraryAssetUrl(assetSrc, sharedLibraryStatic)
 * // Returns: 'https://dev.stokke.com/on/demandware.static/-/Library-Sites-StokkeSharedLibrary/de_DE/v1761758784946/cms_assets/videos/product-demo.mp4'
 *
 * @example
 * // Absolute URL
 * getSharedLibraryAssetUrl('https://cdn.example.com/video.mp4', sharedLibraryStatic)
 * // Returns: 'https://cdn.example.com/video.mp4'
 */
export function getSharedLibraryAssetUrl(assetSrc, sharedLibrary) {
    // Validate input
    if (!assetSrc || typeof assetSrc !== 'string') {
        return null
    }

    // Strip leading slash from relative URLs (but not protocol-relative URLs starting with //)
    // so they are treated as relative to sharedLibrary instead of being treated as absolute
    // paths relative to the origin
    let normalizedSrc =
        assetSrc.startsWith('/') && !assetSrc.startsWith('//') ? assetSrc.slice(1) : assetSrc

    // Escape brackets in relative URLs before passing to URL constructor
    // Only escape if it's a relative URL (doesn't start with protocol)
    // This prevents issues with special characters in filenames like "video(1).mp4"
    if (!isAbsoluteUrl(normalizedSrc)) {
        normalizedSrc = normalizedSrc.replace(/\(/g, '%28').replace(/\)/g, '%29')
    }

    // new URL() with base parameter handles both absolute and relative URLs:
    // - Absolute URLs: base is ignored, returns the absolute URL
    // - Relative URLs: resolves against the base
    try {
        const url = new URL(normalizedSrc, sharedLibrary)
        return url.href
    } catch {
        return null
    }
}

/**
 * Builds the URL for a product set including the color choices and selected optional items.
 * @param {*} product product set
 * @returns URL to the product set
 */
export const productSetUrlBuilder = (product, locale) => {
    if (!product) return undefined
    const baseUrl = productUrlBuilder(product, locale)
    if (!baseUrl || !product.setProducts) return undefined

    const extraParams = {}

    product.setProducts.forEach((childProduct) => {
        const setProductId = childProduct.setProductId
        const childId = childProduct.productId || childProduct.id

        if (setProductId) {
            extraParams[setProductId] = `pid=${childId}`
        }
    })

    const optionalItems = [...(product.setAccessories || []), ...(product.setExtensions || [])]

    optionalItems
        .filter((item) => item.isSetProductSelected === true)
        .forEach((item) => {
            const setProductId = item.setProductId
            const childId = item.productId || item.id
            if (setProductId) {
                extraParams[setProductId] = `pid=${childId}&selected=1`
            }
        })

    // If no params were added, just return the base URL
    if (Object.keys(extraParams).length === 0) return baseUrl

    return rebuildPathWithParams(baseUrl, extraParams)
}
